import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Box,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBack,
  ArrowForward,
} from '@mui/icons-material';
import { modalStyles } from '../styles/modalStyles';
import { LanguageConstants } from '../helpers/LanguageConstants';
import { DATA_SOURCES_DIALOG_Z_INDEX } from '../helpers/zIndexLayers';
import DataSourceStep from './AddSpaceModal/DataSourceStep';
import { useTheme } from '@mui/material/styles';
import { useToast } from '../contexts/ToastContext';
import ConnectedDatasourcesCount from './ConnectedDatasourcesCount';

interface AddLogDataSourceModalProps {
  open: boolean;
  onClose: () => void;
  /** Called when datasources are added; optional pending filenames when first bulk (upload) just succeeded and panel should show them as "In progress" */
  onDataSourceAdded?: (pendingFilenames?: string[]) => void;
  channelId?: string;
}

const AddLogDataSourceModal: React.FC<AddLogDataSourceModalProps> = ({ 
  open, 
  onClose, 
  onDataSourceAdded,
  channelId
}) => {
  const [dataSources, setDataSources] = useState<any[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const dataSourceStepRef = useRef<any>(null);
  const muiTheme = useTheme();
  const { showSuccess } = useToast();
  
  // Step navigation state
  const [fileSelectionStep, setFileSelectionStep] = useState(0);
  const [fileSelectionSteps, setFileSelectionSteps] = useState<string[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedWorkspaceFromStep, setSelectedWorkspaceFromStep] = useState<string>('');
  // Bump key on each open so DataSourceStep remounts (Dialog keepMounted would otherwise keep Drive stepper state)
  const [wizardInstanceKey, setWizardInstanceKey] = useState(0);
  const wasOpenRef = useRef(false);

  // When dialog opens, remount wizard + reset footer mirror state so My Device tab / step 0 show clean
  useLayoutEffect(() => {
    if (open && !wasOpenRef.current) {
      setWizardInstanceKey((k) => k + 1);
      setDataSources([]);
      setIsCreating(false);
      setFileSelectionStep(0);
      setFileSelectionSteps([]);
      setTabValue(0);
      setSelectedWorkspaceFromStep('');
    }
    wasOpenRef.current = open;
  }, [open]);

  // Sync step state and selected channel from DataSourceStep (for footer count when no channelId prop)
  useEffect(() => {
    const syncStepState = () => {
      if (dataSourceStepRef.current) {
        const currentStep = dataSourceStepRef.current.fileSelectionStep ?? 0;
        const steps = dataSourceStepRef.current.fileSelectionSteps ?? [];
        const currentTabValue = dataSourceStepRef.current.tabValue ?? 0;
        const workspace = dataSourceStepRef.current.selectedWorkspace ?? '';
        setFileSelectionStep(currentStep);
        setFileSelectionSteps(steps);
        setTabValue(currentTabValue);
        setSelectedWorkspaceFromStep(workspace);
      }
    };
    
    syncStepState();
    const interval = setInterval(syncStepState, 100);
    return () => clearInterval(interval);
  }, [open]);
  const handleClose = () => {
    setDataSources([]);
    onClose();
  };

  // Called when first bulk API (upload) succeeds: refresh panel with pending filenames, show toaster, then close
  const handleFirstBulkSuccess = (pendingFilenames?: string[]) => {
    onDataSourceAdded?.(pendingFilenames); // Refresh panel and show pending files as "In progress" while 2nd API runs
    showSuccess(
      `${LanguageConstants.datasource_s} added and embedding generation is in progress.`
    );
    // Defer close so panel's setState/refresh runs before modal closes
    setTimeout(() => handleClose(), 0);
  };

  const handleAddDataSource = async () => {
    if (dataSourceStepRef.current?.handleAddDataSource) {
      setIsCreating(true);
      try {
        const success = await dataSourceStepRef.current.handleAddDataSource();
        if (success) {
          // Refresh the connected datasources count
          setRefreshTrigger(prev => prev + 1);
          // Close modal on successful datasource addition
          handleClose();
        }
      } finally {
        setIsCreating(false);
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') {
          return; // Prevent closing when clicking outside
        }
        handleClose();
      }}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      keepMounted={true} // Keep content mounted so create-bulk can complete after close and trigger panel refresh
      // Above DataSourcesPanel's own zIndex (1350), which this modal is opened from
      sx={{ zIndex: DATA_SOURCES_DIALOG_Z_INDEX }}
      PaperProps={{
        sx: modalStyles.dialog['& .MuiDialog-paper'],
      }}
    >
      <DialogTitle sx={modalStyles.dialogTitle}>
        <Typography variant="h5" component="div">
          Add a new {LanguageConstants.datasource}
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={modalStyles.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent 
        sx={{ 
          pt: 0,
          overflow: 'auto',
          maxHeight: '70vh',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(0, 0, 0, 0.3)',
            },
          },
        }}
      >
        <DataSourceStep
          key={wizardInstanceKey}
          ref={dataSourceStepRef}
          dataSources={dataSources}
          onDataSourcesChange={setDataSources}
          onClose={handleClose}
          onDataSourceAdded={onDataSourceAdded}
          onFirstBulkSuccess={handleFirstBulkSuccess}
          channelId={channelId}
          hideInternalButtons={true}
          showStepNavigationButtons={false} // Hide internal buttons, we'll show them in DialogActions
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, position: 'relative' }}>
        {/* Connected Datasources Count Tile - Footer (use selected channel from step when no channelId prop e.g. Admin Hub) */}
        <ConnectedDatasourcesCount 
          channelId={channelId || selectedWorkspaceFromStep || undefined} 
          tabValue={tabValue} 
          refreshTrigger={refreshTrigger}
          variant="footer"
          hasBackButton={fileSelectionStep > 0 && fileSelectionSteps.length > 0}
        />
        
        {/* Step Navigation Buttons - Show when in Google Drive flow */}
        {fileSelectionStep > 0 && fileSelectionSteps.length > 0 ? (
          <>
            <Button
              onClick={() => {
                if (dataSourceStepRef.current?.handleBackStep) {
                  dataSourceStepRef.current.handleBackStep();
                }
              }}
              disabled={!dataSourceStepRef.current?.canGoBack?.() || isCreating}
              startIcon={<ArrowBack />}
              sx={{ textTransform: 'none' }}
            >
              Back
            </Button>
            
            <Box sx={{ flex: 1 }} /> {/* Spacer */}
            
            {fileSelectionStep < fileSelectionSteps.length - 1 ? (
              <Button
                variant="contained"
                onClick={() => {
                  if (dataSourceStepRef.current?.handleNextStep) {
                    dataSourceStepRef.current.handleNextStep();
                  }
                }}
                disabled={!dataSourceStepRef.current?.canGoNext?.() || isCreating}
                endIcon={<ArrowForward />}
                sx={{ textTransform: 'none' }}
              >
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleAddDataSource}
                disabled={isCreating}
                sx={{ textTransform: 'none' }}
              >
                {tabValue === 5 ? (
                  isCreating ? (
                    <>
                      Scraping Started
                      <CircularProgress size={20} color="inherit" sx={{ ml: 1 }} />
                    </>
                  ) : (
                    'Start Scrapping'
                  )
                ) : isCreating ? (
                  <>
                    Adding {LanguageConstants.datasource}
                    <CircularProgress size={20} color="inherit" sx={{ ml: 1 }} />
                  </>
                ) : (
                  `Add ${LanguageConstants.datasource}`
                )}
              </Button>
            )}
          </>
        ) : (
          <>
            {/* Default buttons when not in step flow */}
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={isCreating}
              sx={{ 
                textTransform: 'none',
                fontSize: '0.875rem',
                px: 2,
                py: 0.5,
                borderColor: muiTheme.palette.divider,
                color: muiTheme.palette.text.secondary,
                '&:hover': {
                  borderColor: muiTheme.palette.text.primary,
                  color: muiTheme.palette.text.primary,
                  backgroundColor: muiTheme.palette.action.hover
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAddDataSource}
              disabled={isCreating}
              sx={{ textTransform: 'none' }}
            >
              {tabValue === 5 ? (
                isCreating ? (
                  <>
                    Scraping Started
                    <CircularProgress size={20} color="inherit" sx={{ ml: 1 }} />
                  </>
                ) : (
                  'Start Scrapping'
                )
              ) : isCreating ? (
                <>
                  Adding {LanguageConstants.datasource}
                  <CircularProgress size={20} color="inherit" sx={{ ml: 1 }} />
                </>
              ) : (
                `Add ${LanguageConstants.datasource}`
              )}
            </Button>
          </>
        )}
      </DialogActions>
      
    </Dialog>
  );
};

export default AddLogDataSourceModal; 